import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Rate, Tag } from 'antd';
import API from '../api';
import InitialsAvatar from './InitialsAvatar';

const tx = {
  ar: { title: 'أبرز المستقلين', subtitle: 'محترفون موثوقون جاهزون لمساعدتك', view: 'عرض الملف' },
  en: { title: 'Featured freelancers', subtitle: 'Trusted pros ready to help', view: 'View profile' },
};

export default function FeaturedFreelancers({ lang = 'ar' }) {
  const t = tx[lang] || tx.ar;
  const [list, setList] = useState([]);

  useEffect(() => {
    // Paid featured placements first (⭐), top-rated fill the rest
    API.get('/accounts/featured/?fill=8')
      .then((r) => setList(Array.isArray(r.data) ? r.data : []))
      .catch(() => setList([]));
  }, []);

  const boxRef = useRef(null);
  const drag = useRef({ on: false, x: 0, left: 0, moved: false });
  const paused = useRef(false);

  // auto-scroll to the right (content moves right = scrollLeft decreases), seamless loop over 2 copies
  useEffect(() => {
    const el = boxRef.current;
    if (!el || list.length === 0) return undefined;
    let raf;
    const half = () => el.scrollWidth / 2;
    if (el.scrollLeft === 0) el.scrollLeft = half();
    const step = () => {
      if (!paused.current && !drag.current.on) {
        el.scrollLeft -= 0.6;
        if (el.scrollLeft <= 0) el.scrollLeft += half();
        else if (el.scrollLeft >= half() * 2 - el.clientWidth - 1) el.scrollLeft -= half();
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [list]);

  const onDown = (e) => {
    const el = boxRef.current;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    drag.current = { on: true, x: e.clientX, left: el.scrollLeft, moved: false };
    const move = (ev) => onMove(ev);
    const up = () => { drag.current.on = false; window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up); window.addEventListener('pointercancel', up);
  };
  const onMove = (e) => {
    if (!drag.current.on) return;
    const dx = e.clientX - drag.current.x;
    if (Math.abs(dx) > 5) drag.current.moved = true;
    const el = boxRef.current;
    const half = el.scrollWidth / 2;
    let next = drag.current.left - dx;
    if (next <= 0) { next += half; drag.current.left += half; }
    else if (next >= half * 2 - el.clientWidth - 1) { next -= half; drag.current.left -= half; }
    el.scrollLeft = next;
  };
  const onClickCapture = (e) => { if (drag.current.moved) { e.preventDefault(); e.stopPropagation(); drag.current.moved = false; } };

  if (list.length === 0) return null;

  return (
    <section style={{ padding: '60px 24px', position: 'relative', zIndex: 1, overflow: 'hidden' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <h2 className="grad-text" style={{ fontSize: 28, fontWeight: 800, margin: '0 0 8px' }}>{t.title}</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 15, margin: 0 }}>{t.subtitle}</p>
        </div>
        <div className="ff-marquee" ref={boxRef}
          onPointerDown={onDown}
          onMouseEnter={() => { paused.current = true; }} onMouseLeave={() => { paused.current = false; }}
          onClickCapture={onClickCapture}>
          <div className="ff-track">
          {[...list, ...list].map((f, i) => {
            const name = [f.user?.first_name, f.user?.last_name].filter(Boolean).join(' ').trim() || f.full_name || f.user?.full_name || f.user?.username || f.username || '';
            const avatar = f.avatar || f.user?.avatar;
            const slug = f.username || f.user?.username;
            const headline = f.headline || f.title || (lang === 'ar' ? 'مستقل' : 'Freelancer');
            const rate = (f.hourly_rate || f.user?.hourly_rate) ? `${f.hourly_rate || f.user.hourly_rate} $/hr` : null;
            return (
              <Link to={`/freelancers/${slug}`} key={`${slug || name}-${i}`} className="ff-item">
                <Card hoverable className={`glass-hover${f.featured ? ' ff-featured' : ''}`} style={{ borderRadius: 18, textAlign: 'center', height: 330, display: 'flex', flexDirection: 'column', position: 'relative' }} styles={{ body: { display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 } }}>
                  {f.featured && <span className="ff-badge">★ {lang === 'ar' ? 'مميز' : 'Featured'}</span>}
                  <InitialsAvatar src={avatar} name={name} size={72} style={{ marginBottom: 12 }} />
                  <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: 15 }}>{name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 8px', height: 36, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{headline}</div>
                  {(f.rating_avg || f.rating) ? (
                    <Rate disabled value={f.rating_avg || f.rating} style={{ fontSize: 13 }} />
                  ) : null}
                  <div style={{ marginTop: 'auto', paddingTop: 10 }}>{rate && <Tag color="cyan">{rate}</Tag>}</div>
                </Card>
              </Link>
            );
          })}
          </div>
        </div>
      </div>
    </section>
  );
}
